<script setup>
import { computed, defineProps, onMounted, ref, watch } from "vue";
import apiClient from "@/apiClient";
import { apiUrl } from "@/vars";
import {
  ArrowDownOnSquareIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderArrowDownIcon,
} from "@heroicons/vue/24/solid";

const props = defineProps({
  ticketId: { type: [Number, String], required: true },
  embedded: { type: Boolean, default: false },
  showHeader: { type: Boolean, default: true },
});

const ticket = ref(null);
const loading = ref(false);
const error = ref(null);

const loadTicket = async () => {
  if (!props.ticketId) return;
  loading.value = true;
  error.value = null;
  try {
    const response = await apiClient.get(`/api/ticket/${props.ticketId}`);
    ticket.value = response.data;
  } catch (err) {
    error.value = err.response?.data?.message ?? "Unable to load ticket.";
  } finally {
    loading.value = false;
  }
};

onMounted(loadTicket);
watch(
  () => props.ticketId,
  () => loadTicket(),
);

const subject = computed(() => ticket.value?.subject ?? `Ticket #${props.ticketId}`);

const getDeliveryStatus = (item) => {
  const email = item?.latest_email;
  if (!email) return null;
  if ((email.opens ?? 0) > 0 || email.is_read) {
    return { label: "Opened", classes: "bg-emerald-100 text-emerald-800" };
  }
  if (email.delivered) {
    return { label: "Delivered", classes: "bg-sky-100 text-sky-800" };
  }
  return { label: "Sent", classes: "bg-slate-200 text-slate-700" };
};

const quoteSendMetadata = (item) =>
  (Array.isArray(item?.metadata) ? item.metadata : []).find(
    (entry) => entry?.key === "quote_send" && (entry?.quote || entry?.value?.quote_id),
  ) || null;

const quoteFromMetadata = (metadata) => {
  if (!metadata) return null;

  return {
    id: metadata.quote?.id ?? metadata.value?.quote_id ?? metadata.subject_id,
    status: metadata.quote?.status ?? metadata.value?.status_at_send,
    display_name:
      metadata.quote?.display_name ||
      metadata.quote?.description ||
      metadata.value?.reference_text ||
      "Quote",
    reference_text:
      metadata.quote?.reference_text || metadata.value?.reference_text || null,
  };
};

const quoteStatusLabel = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "accepted") return "Accepted";
  if (value === "declined") return "Declined";
  if (value === "sent") return "Sent";
  if (value === "draft") return "Draft";
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "Quote";
};

const quoteStatusClasses = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "accepted") {
    return "bg-emerald-100 text-emerald-800";
  }
  if (value === "declined") {
    return "bg-rose-100 text-rose-800";
  }
  if (value === "sent") {
    return "bg-sky-100 text-sky-800";
  }
  return "bg-slate-200 text-slate-700";
};

const inlineViewableAttachmentExtensions = new Set([
  "bmp",
  "gif",
  "jpeg",
  "jpg",
  "pdf",
  "png",
  "svg",
  "webp",
]);

const attachmentSupportsInlineViewing = (attachment) => {
  const filename = String(attachment?.file?.filename || "");
  const extension = filename.split(".").pop()?.toLowerCase() || "";

  return inlineViewableAttachmentExtensions.has(extension);
};

const attachmentUrl = (attachment, options = {}) => {
  const params = new URLSearchParams();
  if (options.download) {
    params.set("download", "1");
  }

  const query = params.toString();

  return (
    `${apiUrl}/ticket/${ticket.value.id}/attachment/${attachment.id}` +
    (query ? `?${query}` : "")
  );
};

const attachmentApiPath = (attachment) =>
  `/api/ticket/${ticket.value.id}/attachment/${attachment.id}`;

const attachmentInlineMimeType = (attachment) => {
  const filename = String(attachment?.file?.filename || "").toLowerCase();

  if (filename.endsWith(".pdf")) {
    return "application/pdf";
  }

  if (filename.endsWith(".png")) {
    return "image/png";
  }

  if (filename.endsWith(".gif")) {
    return "image/gif";
  }

  if (filename.endsWith(".webp")) {
    return "image/webp";
  }

  if (filename.endsWith(".svg")) {
    return "image/svg+xml";
  }

  return "image/jpeg";
};

const openAttachment = async (attachment) => {
  const previewWindow = window.open("", "_blank");

  try {
    if (!previewWindow) {
      throw new Error("Preview window was blocked by the browser.");
    }

    previewWindow.document.title = "Opening attachment...";
    previewWindow.document.body.innerHTML =
      '<div style="font-family: sans-serif; padding: 24px; color: #0f172a;">Opening attachment...</div>';

    const response = await apiClient.get(attachmentApiPath(attachment), {
      responseType: "blob",
    });
    const sourceBlob =
      response.data instanceof Blob ? response.data : new Blob([response.data]);
    const objectUrl = window.URL.createObjectURL(
      sourceBlob.type && sourceBlob.type !== "application/octet-stream"
        ? sourceBlob
        : new Blob([sourceBlob], {
            type: attachmentInlineMimeType(attachment),
          }),
    );

    previewWindow.location.replace(objectUrl);

    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 300_000);
  } catch (error) {
    if (previewWindow) {
      previewWindow.close();
    }

    alert(error?.response?.data?.message ?? "Unable to open attachment.");
  }
};
</script>

<template>
  <div :class="props.embedded ? 'space-y-3' : 'space-y-4'">
    <div
      v-if="props.showHeader"
      :class="
        props.embedded
          ? 'rounded-lg border border-slate-200 dark:border-slate-700 p-4'
          : 'box'
      "
    >
      <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.2em] text-slate-400">Ticket</p>
          <h2
            :class="
              props.embedded
                ? 'text-lg font-semibold text-slate-900 dark:text-slate-100'
                : 'text-xl font-semibold text-slate-900 dark:text-slate-100'
            "
          >
            {{ subject }}
          </h2>
          <div class="text-sm text-slate-500">
            <span>{{ ticket?.status ?? "Open" }}</span>
            <span v-if="ticket?.created_at">• Created {{ ticket.created_at }}</span>
          </div>
        </div>
        <RouterLink :to="`/tickets/${ticketId}`" class="btn">Open ticket</RouterLink>
      </div>
    </div>

    <div v-if="error" class="box text-sm text-rose-500">{{ error }}</div>
    <div
      v-else-if="loading"
      :class="props.embedded ? 'rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-500' : 'box text-sm text-slate-500'"
    >
      Loading ticket...
    </div>
    <div v-else-if="ticket?.items?.length" class="space-y-4">
      <div class="box" v-for="item in ticket.items.slice().reverse()" :key="item.id">
        <div class="flex justify-between">
          <div v-if="item.contact">
            <b>
              <RouterLink :to="'/contact/' + item.contact.id">
                {{ item.contact?.name }} &lt;{{ item.contact?.email ?? "Unknown Contact" }}&gt;
              </RouterLink>
            </b>
            &nbsp; to &nbsp; <b>Medic 1 Team</b>
          </div>
          <div v-else>
            <b>{{ item.user?.name }} &lt;{{ item.user?.email ?? "Unknown User" }}&gt;</b>
            <template v-if="item.sendAnonymously === 1">&nbsp;
              <span>via Medic 1 Team &lt;sales@medic1.uk&gt;</span>
            </template>
            <template v-if="item.recipient">
              &nbsp; to &nbsp;
              <b>
                <RouterLink :to="'/contact/' + item.recipient.id">
                  {{ item.recipient.name }} &lt;{{ item.recipient.email }}&gt;
                </RouterLink>
              </b>
              <template v-if="getDeliveryStatus(item)">
                &nbsp;
                <span :class="[
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                  getDeliveryStatus(item).classes,
                ]">
                  {{ getDeliveryStatus(item).label }}
                </span>
              </template>
            </template>
          </div>
          <div>
            <TimeAgo :data="item.created_at" />
          </div>
        </div>
        <hr class="my-2" />
        <div v-if="item.html" v-html="item.html" class="break-all"></div>
        <span v-else class="whitespace-pre-wrap break-all">{{ item.text }}</span>

        <div
          v-if="quoteSendMetadata(item)"
          class="mt-3 flex flex-wrap items-center gap-3 border-l-4 border-l-cyan-500 bg-cyan-50 px-3 py-2 text-sm"
        >
          <DocumentTextIcon class="h-5 w-5 shrink-0 text-cyan-700" />
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-semibold text-slate-900">Quote sent</span>
              <span
                :class="[
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
                  quoteStatusClasses(quoteFromMetadata(quoteSendMetadata(item))?.status),
                ]"
              >
                {{ quoteStatusLabel(quoteFromMetadata(quoteSendMetadata(item))?.status) }}
              </span>
            </div>
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
              <RouterLink
                v-if="quoteFromMetadata(quoteSendMetadata(item))?.id"
                :to="`/quotes/${quoteFromMetadata(quoteSendMetadata(item)).id}`"
                class="font-semibold text-cyan-800 hover:text-cyan-950"
              >
                {{ quoteFromMetadata(quoteSendMetadata(item))?.display_name }}
              </RouterLink>
              <span v-if="quoteFromMetadata(quoteSendMetadata(item))?.reference_text">
                {{ quoteFromMetadata(quoteSendMetadata(item))?.reference_text }}
              </span>
            </div>
          </div>
        </div>

        <hr v-if="item.attachments?.length" class="my-2" />
        <div
          v-for="attachment in item.attachments"
          :key="attachment.id"
          class="mb-2 flex flex-wrap items-center gap-2"
        >
          <template v-if="attachmentSupportsInlineViewing(attachment)">
            <button
              type="button"
              class="label-bubble inline-flex cursor-pointer items-center gap-2 hover:brightness-110"
              @click="openAttachment(attachment)"
            >
              <EyeIcon class="icon"></EyeIcon>
              {{ attachment.file.filename }}
            </button>
            <a
              :href="attachmentUrl(attachment, { download: true })"
              class="label-bubble inline-flex items-center gap-2 text-slate-500 hover:text-slate-900"
            >
              <ArrowDownOnSquareIcon class="icon"></ArrowDownOnSquareIcon>
              Download
            </a>
          </template>
          <a
            v-else
            :href="attachmentUrl(attachment, { download: true })"
            class="label-bubble inline-flex items-center gap-2 hover:brightness-110"
          >
            <ArrowDownOnSquareIcon class="icon"></ArrowDownOnSquareIcon>
            {{ attachment.file.filename }}
          </a>
        </div>
        <div v-if="item.attachments && item.attachments.length > 1">
          <a class="label-bubble" :href="apiUrl + '/ticket/' + ticket.id + '/attachments/' + item.id">
            <FolderArrowDownIcon class="icon"></FolderArrowDownIcon> Download All
          </a>
        </div>
      </div>
    </div>
    <div
      v-else
      :class="props.embedded ? 'rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm text-slate-500' : 'box text-sm text-slate-500'"
    >
      No ticket items yet.
    </div>
  </div>
</template>
